import type { ServerResponse } from 'http';
import type { DocumentContext } from 'next/document';
import { t } from '../locale/texts';

export type ResponseWithLocals = ServerResponse & {
    // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
    locals?: Record<string, any>;
};

export type DocumentContextWithResLocals = DocumentContext & {
    res?: ResponseWithLocals;
};

export type TripType = 'roundtrip' | 'oneway' | 'openjaw';

export const tripTypesOptions = [
    { value: 'roundtrip', label: t('flight.tripType.roundtrip') },
    { value: 'oneway', label: t('flight.tripType.oneway') },
    { value: 'openjaw', label: t('flight.tripType.openjaw') },
];

export const cabinTypesOptions = [
    { value: 'economy', label: t('flight.cabinType.economy') },
    { value: 'premium', label: t('flight.cabinType.premium') },
    { value: 'business', label: t('flight.cabinType.business') },
    { value: 'first', label: t('flight.cabinType.first') },
    { value: 'all', label: t('flight.cabinType.all') },
];

export interface AutocompleteBounds {
    southwest_lat: number;
    southwest_lng: number;
    northeast_lat: number;
    northeast_lng: number;
    latitude?: number;
    longitude?: number;
}

export type BaseAutocompleteOption = {
    id: string;
    text: string;
};

export type FlightOption = {
    locationType: 'AIRPORT' | 'CITY' | 'UNKNOWN';
    city: string;
    country: string;
} & BaseAutocompleteOption;

export type FhhOption = {
    count: number;
    bounds: AutocompleteBounds;
    city: string;
    country: string;
} & BaseAutocompleteOption;

export type CarOption = {
    type: string; // viewtype not pure data...
    typeLogo: string | null;
    code: string;
    placeId: string;
    latitude: string;
    longitude: string;
} & BaseAutocompleteOption;

export type HotelOption = {
    key?: string;
    type: string; // unsafe, should probably be 'hotel' | 'place'
    hierarchy: string[];
    searchable: boolean;
    themePath?: string;
} & BaseAutocompleteOption;

export type AutocompleteOption = Partial<FlightOption & FhhOption & CarOption & HotelOption> & BaseAutocompleteOption;

/**
 * AutocompleteResponse
 * Possible expected types
 * - FlightOption
 *  value: string;
 *  text: string;
 *  cityName: string;
 *  countryName: string;
 *
 * - FhhOption
 *  primary: string;
 *  secondary: string | null;
 *  destination: string;
 *  numResults: number;
 *  bounds: AutocompleteBounds;
 *  city: string | null;
 *  country: string;
 *
 *  - CarOption
 *  code: string;
 *  name: string;
 *  type: string;
 *  code: string;
 *  placeId: string;
 *  latitude: string;
 *  longitude: string;
 *
 *  - HotelOption
 *  type: string;
 *  text: string;
 *  hierarchy: string[];
 *  searchable: boolean;
 *  key?: string;
 *  themePath?: string;
 *
 *  - ppOption
 *  id: string;
 *  text: string;
 *
 *  but we can't really know for sure since they come from different APIs, so it should be unknown
 */
export type AutocompleteResponse = Record<string, unknown>[];

export type AutocompleteProps = {
    id: string;
    label: string;
    defaultValue?: string | null;
    placeholder: string;
    error?: string;
    url: string;
    headers?: HeadersInit;
    mapOptions: (options: AutocompleteResponse) => AutocompleteOption[];
    onSelect: (option: AutocompleteOption | null, typedText?: string) => void;
    minChars?: number;
    vertical: 'flight' | 'hotel' | 'car' | 'fhh' | 'prepackage';
    departure?: boolean;
    showDefaultOptionOnFocus?: boolean;
};
